import type { PlaybookNodeId } from "@repo/app-types/entities/playbook";
import { IconArrowRight, IconChevronDown } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { twMerge } from "tailwind-merge";

export type PlaybookDraftSuggestionKind =
  | "existing_criterion"
  | "new_criterion"
  | "existing_section"
  | "new_section"
  | "outcome";

export interface PlaybookDraftSuggestion {
  id: PlaybookNodeId;
  name: string;
  kind: PlaybookDraftSuggestionKind;
}

/**
 * The node currently selected in the editor, normalized into the suggestion
 * id-space so a row can tell whether it's the one being viewed. `id` is
 * synthetic for outcomes (a playbook has a single outcome pane) and is only
 * compared for the other kinds.
 */
export interface ViewedPlaybookNode {
  kind: PlaybookDraftSuggestionKind;
  id: string;
}

export function isSuggestionViewed(
  suggestion: PlaybookDraftSuggestion,
  viewed: ViewedPlaybookNode | null | undefined,
): boolean {
  if (!viewed || suggestion.kind !== viewed.kind) return false;
  return suggestion.kind === "outcome" || suggestion.id === viewed.id;
}

export interface PlaybookDraftSuggestionListProps {
  suggestions: readonly PlaybookDraftSuggestion[];
  onSelect: (suggestion: PlaybookDraftSuggestion) => void;
  /** The node currently selected in the editor, to flag the "Viewing" row. */
  viewedNode?: ViewedPlaybookNode | null;
  className?: string;
}

export const PlaybookDraftSuggestionList: FC<
  PlaybookDraftSuggestionListProps
> = ({ suggestions, onSelect, viewedNode, className }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div
      className={twMerge(
        "w-full min-w-0 overflow-hidden rounded-lg border border-border-light bg-white shadow-xs",
        className,
      )}
    >
      <div className="flex min-w-0 items-center justify-between gap-2 px-3 py-1.5">
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-muted-foreground">
          Playbook suggestions
        </span>
        <button
          type="button"
          onClick={() => {
            setIsCollapsed((prev) => !prev);
          }}
          aria-expanded={!isCollapsed}
          aria-label={
            isCollapsed ? "Expand suggestions" : "Collapse suggestions"
          }
          className="-mr-1 flex size-5 shrink-0 items-center justify-center rounded-sm text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
          <IconChevronDown
            className={twMerge(
              "size-4 transition-transform duration-200 ease-out",
              isCollapsed && "-rotate-90",
            )}
          />
        </button>
      </div>
      <div
        className={twMerge(
          "grid transition-[grid-template-rows,opacity] duration-200 ease-out",
          isCollapsed
            ? "grid-rows-[0fr] opacity-0"
            : "grid-rows-[1fr] opacity-100",
        )}
      >
        <ul className="flex min-w-0 flex-col overflow-hidden border-t border-gray-100">
          {suggestions.map((suggestion, index) => (
            <li key={suggestion.id} className="min-w-0">
              <PlaybookDraftSuggestionRow
                suggestion={suggestion}
                isFirst={index === 0}
                isViewing={isSuggestionViewed(suggestion, viewedNode)}
                onSelect={onSelect}
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

interface PlaybookDraftSuggestionRowProps {
  suggestion: PlaybookDraftSuggestion;
  isFirst: boolean;
  isViewing: boolean;
  onSelect: (suggestion: PlaybookDraftSuggestion) => void;
}

const PlaybookDraftSuggestionRow: FC<PlaybookDraftSuggestionRowProps> = ({
  suggestion,
  isFirst,
  isViewing,
  onSelect,
}) => {
  return (
    <button
      type="button"
      disabled={isViewing}
      onClick={() => {
        onSelect(suggestion);
      }}
      className={twMerge(
        "group flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-indigo-600",
        isViewing
          ? "cursor-default text-gray-400"
          : "text-foreground hover:bg-gray-50",
        !isFirst && "border-t border-gray-100",
      )}
    >
      <span className="min-w-0 flex-1 truncate" title={suggestion.name}>
        {suggestion.name}
      </span>
      {isViewing ? (
        <span className="shrink-0 text-xs text-gray-400">Viewing</span>
      ) : (
        <IconArrowRight className="size-4 shrink-0 text-gray-400 group-hover:text-gray-600" />
      )}
    </button>
  );
};
